import { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  HRISProviderSlugEnum,
  EmployeeStatusEnum,
  RequestTypeCategorySlugEnum,
  EntitlementSlugEnum,
} from '@electricjs/core_entity-client';

import {
  Box,
  BoxProps,
  Button,
  Flex,
  IconButton,
  SearchAsyncSelectField,
  spaces,
  Tooltip,
} from '@electricjs/arc';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { SearchHintOption } from '@/components/SearchHintOption';
import { EmployeeOption } from '@/components/People/EmployeeGroups/NewEmployeeGroupForm';
import {
  transformEmployeesToOptions,
  appendSearchHint,
  EMPLOYEE_SEARCH_HINT_VALUE,
} from '@/components/People/transformOptions';

import { SelectionParamKeys } from '@/types/queryParamKeys';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGetOrganizationPackagesQuery } from '@/redux/slices/packageApiSlice';
import {
  useGetOrganizationEmployeesQuery,
  useLazyGetOrganizationEmployeesQuery,
  useGetOrganizationQuery,
} from '@/redux/slices/organizationApiSlice';
import { useLazyGetEmployeeByIdQuery } from '@/redux/slices/employeeApiSlice';
import useIntegrationProvider from '@/components/Settings/IntegrationsPane/HRISIntegrations/useIntegrationProvider';
import {
  useSyncHrisIntegrationV2Mutation,
  useGetSyncStatusQuery,
} from '@/redux/slices/hrisIntegrationApiSlice';
import { useGetMyRequestsOrGetAllRequests } from '@/hooks/useGetMyRequestsOrGetAllRequests';
import { RequestStatus } from '@/types/requests';

import { useStorefront } from '../../StorefrontProvider';
import { emailSeparator } from '../StorefrontCheckoutFormProvider';
import { skipToken } from '@reduxjs/toolkit/query';
import { EmployeeSelectOption } from '@/components/EmployeeSelectOption';
import { useIsEmbedded } from '@/hooks/useIsEmbedded';
import { useIsAdpWfnCanadaConnectedOrg } from '@/hooks/useIsAdpWfnCanadaConnectedOrg';
import { getShoppingCountryOptions } from '../ShoppingCountrySelector';

const SyncButton = styled(Button)`
  position: absolute;
  right: 0;
  top: -2px;
`;

/**
 Defines what type of sync is being performed,
 either manual, from the `Sync` button or
 automatically triggered , as a failover,
 when the hrisEmployeeId is missing.
 */
const SyncType = {
  Manual: 'manual',
  EmployeeSearch: 'employee-search',
} as const;

type SyncType = (typeof SyncType)[keyof typeof SyncType];

type EmployeeSectionProps = Pick<BoxProps, 'width'>;

export const EmployeeSection = ({ width = '100%' }: EmployeeSectionProps) => {
  const { t, i18n } = useTranslation('storefront');
  const isAdpWfnCanada = useIsAdpWfnCanadaConnectedOrg();
  const {
    storefrontCheckoutForm,
    otherCountryFlowFirstStepForm,
    clearAllCarts,
  } = useStorefront();

  const selectedEmployee = storefrontCheckoutForm?.watch('employee');

  const navigate = useNavigate();

  const { storefrontInternational } = useFlags();

  const { showSuccessToast, showErrorToast, showToast } = useGlobalUI();

  const organizationId = useGetOrganizationId();

  const {
    data: organizationPackages,
    isLoading: isLoadingOrganizationPackages,
  } = useGetOrganizationPackagesQuery(organizationId);

  const [queryParams, setQueryParams] = useSearchParams();

  const employeeId = queryParams.get(SelectionParamKeys.EmployeeId);

  const hrisEmployeeId = queryParams.get(SelectionParamKeys.HrisEmployeeId);

  // Fetching all employees at once (still needed in order to find the employee based on hrisEmployeeId)
  // TODO: remove once we have an endpoint to get employee data by hrisEmployeeId
  const {
    data: employees,
    isFetching: isFetchingEmployees,
    refetch: refetchEmployees,
  } = useGetOrganizationEmployeesQuery({
    organizationId,
    // Include UNKNOWN status when searching by hrisEmployeeId
    ...(hrisEmployeeId && {
      status: [EmployeeStatusEnum.Active, EmployeeStatusEnum.Unknown],
    }),
  });

  const [searchForEmployees, { isFetching: isSearchingEmployees }] =
    useLazyGetOrganizationEmployeesQuery();

  const [getEmployeeById, { isFetching: isFetchingEmployee }] =
    useLazyGetEmployeeByIdQuery();

  const [syncHrisIntegration, { isLoading: isSyncing }] =
    useSyncHrisIntegrationV2Mutation();
  const { hrisIntegrationId, provider, isLoadingProvider } =
    useIntegrationProvider();

  // Get organization data to check if HRIS integration has synced
  const { data: orgData } = useGetOrganizationQuery(organizationId, {
    skip: !organizationId,
  });

  const hasHrisIntegrationSynced =
    orgData?.hrisIntegration?.lastSync !== undefined &&
    orgData?.hrisIntegration?.lastSync !== null;

  // Find employee by HRIS ID when hrisEmployeeId is present
  // TODO: Don't try to find from an employee list anymore. Use an endpoint to get employee data by hrisEmployeeId.
  const employee = hrisEmployeeId
    ? employees?.find(emp => emp.hrisIntegration?.sourceId === hrisEmployeeId)
    : undefined;
  const employeeNotFound =
    !!hrisEmployeeId && !isFetchingEmployees && !employee;

  // Task lookup for pending onboarding tasks
  const {
    data: pendingOnboardingTasks,
    isLoading: isLoadingTasks,
    error: tasksError,
  } = useGetMyRequestsOrGetAllRequests({
    params:
      employee && hrisEmployeeId
        ? {
            organizationId,
            status: RequestStatus.InProgress,
            categorySlug: RequestTypeCategorySlugEnum.Onboarding,
            onBehalfOf: employee.id,
          }
        : skipToken,
    queryAll: true,
  });

  // State to track current workflow ID being monitored
  const [currentWorkflowId, setCurrentWorkflowId] = useState<string | null>(
    null
  );

  const [syncType, setSyncType] = useState<SyncType | null>(null);
  const [hasTriggeredFailoverSync, setHasTriggeredFailoverSync] =
    useState(false); // This is to ensure we only run it once.
  const [isRunningFailoverSync, setIsRunningFailoverSync] = useState(false); // This is to avoid racing conditions during the sync.
  const [hasShownErrorToast, setHasShownErrorToast] = useState(false); // Prevent infinite error toast

  const { isEmbedded } = useIsEmbedded();

  // Reset flags when hrisEmployeeId changes
  useEffect(() => {
    setHasTriggeredFailoverSync(false);
    setIsRunningFailoverSync(false);
    setHasShownErrorToast(false);
  }, [hrisEmployeeId]);

  // RTK Query hook for polling sync status
  const {
    data: syncStatus,
    isError: isSyncStatusError,
    error: syncStatusError,
    isFetching: isFetchingSyncStatus,
  } = useGetSyncStatusQuery(
    {
      organizationId,
      hrisIntegrationId: hrisIntegrationId!,
      workflowId: currentWorkflowId!,
    },
    {
      skip: !currentWorkflowId || !hrisIntegrationId,
      // Poll every 5 seconds - RTK Query handles this automatically
      pollingInterval: 5000,
    }
  );

  // Handle sync status changes
  useEffect(() => {
    if (!syncStatus || !currentWorkflowId || !syncType || isFetchingSyncStatus)
      return;

    const status = syncStatus.status;

    if (status === 'COMPLETED') {
      // Sync completed successfully
      setCurrentWorkflowId(null); // Stop polling

      if (syncType === SyncType.Manual) {
        showSuccessToast({
          id: 'sync-completed-toast',
          message: t('sections.employee.toasts.syncCompleted', {
            provider: provider?.name,
          }),
        });
      } else if (syncType === SyncType.EmployeeSearch) {
        // Employee search failover sync - refetch employees to check if we can now find the employee.
        refetchEmployees();
        setIsRunningFailoverSync(false);
      }

      setSyncType(null); // Reset sync type
    } else if (status === 'PENDING') {
      // Sync is still in progress - continue polling
      // Don't show toast repeatedly, just continue monitoring
    } else if (status === 'FAILED') {
      // Sync failed
      setCurrentWorkflowId(null); // Stop polling
      setSyncType(null); // Reset sync type
      setIsRunningFailoverSync(false);
      console.error(`Sync failed with status: ${status}`, syncStatus.message);

      // Manual sync failed - show generic sync error
      showErrorToast({
        id: 'sync-failed-toast',
        message: syncStatus.message || t('sections.employee.toasts.syncFailed'),
      });
    }
  }, [
    syncStatus,
    currentWorkflowId,
    syncType,
    provider,
    showSuccessToast,
    showErrorToast,
    refetchEmployees,
    isFetchingSyncStatus,
    t,
  ]);

  // Handle sync status polling errors
  useEffect(() => {
    if (isSyncStatusError && currentWorkflowId) {
      console.error('Error checking sync status:', syncStatusError);
      setCurrentWorkflowId(null); // Stop polling
      setSyncType(null); // Reset sync type
      setIsRunningFailoverSync(false);
      showErrorToast({
        id: 'sync-status-error-toast',
        message: t('sections.employee.toasts.syncStatusError'),
      });
    }
  }, [
    isSyncStatusError,
    syncStatusError,
    currentWorkflowId,
    showErrorToast,
    t,
  ]);

  const handleSync = useCallback(
    async (type: SyncType = SyncType.Manual) => {
      if (!hrisIntegrationId) return;

      try {
        if (type === SyncType.EmployeeSearch) {
          showToast({
            id: 'searching-employee-toast',
            intent: 'info',
            message: t('sections.employee.toasts.searchingEmployee'),
          });
        }
        const workflowId = await syncHrisIntegration({
          organizationId,
          hrisIntegrationId,
          filterByExternalIds: hrisEmployeeId ? [hrisEmployeeId] : [],
        }).unwrap();

        // Start polling with the returned workflow ID
        setSyncType(type);
        setCurrentWorkflowId(workflowId);
        setHasShownErrorToast(false);
      } catch (error) {
        console.error('Error starting sync:', error);
        showErrorToast({
          id: 'sync-start-error-toast',
          message: t('sections.employee.toasts.syncFailed'),
        });
      }
    },
    [
      hrisIntegrationId,
      syncHrisIntegration,
      organizationId,
      hrisEmployeeId,
      setSyncType,
      setCurrentWorkflowId,
      showErrorToast,
      showToast,
      t,
    ]
  );

  const handleFailoverSync = useCallback(() => {
    handleSync(SyncType.EmployeeSearch);
  }, [handleSync]);

  const resetShippingDetails = useCallback(() => {
    // Resets all shipping details (using "setValue", because "resetField" was not working)
    storefrontCheckoutForm?.setValue('shippingName', '');
    storefrontCheckoutForm?.setValue('shippingPhone', '');
    storefrontCheckoutForm?.setValue('shippingAddress', {
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      country: '',
      zip: '',
    });
    storefrontCheckoutForm?.setValue('shippingEmail', '');
    storefrontCheckoutForm?.setValue('catalogShippingMethod', undefined);
    storefrontCheckoutForm?.setValue('warehouseShippingMethod', undefined);
    storefrontCheckoutForm?.setValue('notes', '');

    otherCountryFlowFirstStepForm?.reset();
  }, [otherCountryFlowFirstStepForm, storefrontCheckoutForm]);

  // Populate shipping details based on employee information
  const populateShippingDetails = useCallback(
    (employee: Partial<EmployeeOption>) => {
      // first, reset the shipping details
      resetShippingDetails();

      // populate with employee data
      if (employee) {
        const { name, address, email, personalEmail, phone } = employee;

        storefrontCheckoutForm?.setValue('shippingName', name);

        storefrontCheckoutForm?.setValue(
          'shippingPhone',
          phone?.replace('+1 ', '') // For now, the checkout page handles only US (and, soon, Canada), so it will work. When we allow other countries besides them, the entire phone field will be replaced by the international phone component
        );

        const emails = [email, personalEmail].filter(item => item); // creates the array of emails and remove the undefined or blank ones;
        storefrontCheckoutForm?.setValue(
          'shippingEmail',
          emails.join(`${emailSeparator} `)
        );

        otherCountryFlowFirstStepForm?.setValue(
          'email',
          personalEmail ?? email ?? ''
        );

        otherCountryFlowFirstStepForm?.setValue('phone', {
          phoneNumber: phone ?? '',
        });

        if (address) {
          // automatically sets the shopping country based on employee's country (when international feature-flag is enabled)
          if (storefrontInternational) {
            // try to find the option from the available ones (configured in the "constants" file)
            const shoppingCountryOption = getShoppingCountryOptions().find(
              option => option.value === address.country
            );

            if (shoppingCountryOption) {
              // set the corresponding shopping country option
              storefrontCheckoutForm?.setValue(
                'shoppingCountry',
                shoppingCountryOption
              );

              // set the storefront regular flow shipping address
              storefrontCheckoutForm?.setValue('shippingAddress', {
                ...address,
              });
            } else {
              // set the "Other countries" option
              storefrontCheckoutForm?.setValue(
                'shoppingCountry',
                getShoppingCountryOptions().find(
                  option => option.value === 'other'
                )
              );

              // set the "other country flow" address
              otherCountryFlowFirstStepForm?.setValue('address', {
                ...address,
              });
            }
          } else {
            // when international feature-flag is disabled: only sets the storefront regular flow shipping address (and only for US address)
            if (address.country === 'US') {
              storefrontCheckoutForm?.setValue('shippingAddress', {
                ...address,
              });
            }
          }
        }
      }
    },
    [
      otherCountryFlowFirstStepForm,
      resetShippingDetails,
      storefrontCheckoutForm,
      storefrontInternational,
    ]
  );

  // Set employee's MDM entitlement (when org packages are loaded)
  useEffect(() => {
    if (!selectedEmployee) {
      storefrontCheckoutForm?.setValue('employeeHasMdmEntitlement', undefined); // reset to undefined ("resetField" was not working for this field)
      storefrontCheckoutForm?.setValue(
        'employeeHasWAutopilotEnrollmentEntitlement',
        undefined
      );
      return;
    }

    if (
      selectedEmployee &&
      !isLoadingOrganizationPackages &&
      organizationPackages
    ) {
      const employeeEntitlements = organizationPackages.find(
        orgPackage => orgPackage.id === selectedEmployee.organizationPackageId
      )?.package.entitlements;

      const hasMdmEntitlement = employeeEntitlements?.some(
        entitlement => entitlement.slug === EntitlementSlugEnum.JumpcloudMdm
      );

      storefrontCheckoutForm?.setValue(
        'employeeHasMdmEntitlement',
        hasMdmEntitlement
      );

      const hasWAutopilotEnrollmentEntitlement = employeeEntitlements?.some(
        entitlement =>
          entitlement.slug === EntitlementSlugEnum.AutopilotDeviceEnrollment
      );

      storefrontCheckoutForm?.setValue(
        'employeeHasWAutopilotEnrollmentEntitlement',
        hasWAutopilotEnrollmentEntitlement
      );
    }
  }, [
    selectedEmployee,
    organizationPackages,
    storefrontCheckoutForm,
    isLoadingOrganizationPackages,
  ]);

  // If employeeId param is passed in the URL and the form values for employee is empty, preloads employee in the dropdown and fill in the shipping details
  useEffect(() => {
    const initializeWithEmployee = async () => {
      if (employeeId && !storefrontCheckoutForm?.getValues('employee')?.value) {
        try {
          const employee = await getEmployeeById({
            employeeId,
            organizationId,
          }).unwrap();

          const employeeOption = transformEmployeesToOptions(
            [employee],
            i18n.language,
            isAdpWfnCanada
          )[0];

          if (employeeOption) {
            storefrontCheckoutForm?.setValue('employee', employeeOption);
            populateShippingDetails(employeeOption);
          }
        } catch (e) {
          showErrorToast({
            id: 'employee-initialization-error-toast',
            message: t('sections.employee.toasts.employeeInitializationError'),
          });
        }
      }
    };

    initializeWithEmployee();
  }, [
    employeeId,
    getEmployeeById,
    organizationId,
    populateShippingDetails,
    storefrontCheckoutForm,
    showErrorToast,
    t,
    i18n.language,
    isAdpWfnCanada,
  ]);

  // Handle redirects and errors for HRIS employee ID workflow
  useEffect(() => {
    if (!hrisEmployeeId || isFetchingEmployees || isLoadingTasks) {
      return;
    }

    // Handle task lookup errors first
    if (tasksError) {
      console.error('Error looking up tasks:', tasksError);
      showErrorToast({
        id: 'task-lookup-error',
        message: t('sections.employee.toasts.taskLookupError'),
      });
      return;
    }

    // If employee is not found, attempt failover sync first (if not already attempted)
    if (employeeNotFound) {
      // Don't attempt failover sync if an HRIS integration exists but hasn't synced yet.
      // Let the automatic backend sync complete first to avoid race conditions.
      // When no HRIS integration exists (no hrisIntegrationId), fall through to show the error toast.
      if (hrisIntegrationId && !hasHrisIntegrationSynced) {
        return;
      }

      const shouldAttemptFailoverSync =
        hrisIntegrationId &&
        !currentWorkflowId && // If set, another sync is already in progress.
        !hasTriggeredFailoverSync && // If set, the failover sync has already been triggered, we dont want to run it twice.
        !isRunningFailoverSync; // If set, the failover sync is already running.
      if (shouldAttemptFailoverSync) {
        setHasTriggeredFailoverSync(true);
        setIsRunningFailoverSync(true);
        handleFailoverSync();
        return;
      } else if (
        !currentWorkflowId &&
        !isRunningFailoverSync &&
        !hasShownErrorToast
      ) {
        // Only show error after running the failover sync, if no sync is currently in progress, and if not already shown.
        setHasShownErrorToast(true);
        showErrorToast({
          id: 'employee-not-found-error',
          message: t('sections.employee.toasts.employeeNotFound'),
        });
        return;
      }
    }

    // If employee is found, check for pending onboarding tasks
    if (
      pendingOnboardingTasks?.results &&
      pendingOnboardingTasks.results.length > 0
    ) {
      // Redirect to the first pending onboarding task
      const firstTask = pendingOnboardingTasks.results[0];
      navigate(`/tasks/${firstTask.id}`);
      return;
    }
    // If no pending tasks found, set employee in dropdown.
    // Active employees are always selectable.
    // Unknown (pending) employees are selectable when the storefront is embedded.
    const isSelectableEmployee =
      employee?.status === EmployeeStatusEnum.Active ||
      (isEmbedded && employee?.status === EmployeeStatusEnum.Unknown);

    if (isSelectableEmployee && storefrontCheckoutForm) {
      // Transform employee to option format
      const employeeOption = transformEmployeesToOptions(
        [employee],
        i18n.language,
        isAdpWfnCanada
      )[0];

      // Set the employee in the dropdown
      storefrontCheckoutForm.setValue('employee', employeeOption);

      // Populate shipping details
      populateShippingDetails(employeeOption);

      // Remove the hrisEmployeeId from URL
      queryParams.delete(SelectionParamKeys.HrisEmployeeId);
      setQueryParams(queryParams);
    }
  }, [
    hrisEmployeeId,
    isFetchingEmployees,
    isLoadingTasks,
    tasksError,
    employeeNotFound,
    employee,
    pendingOnboardingTasks,
    navigate,
    showErrorToast,
    storefrontCheckoutForm,
    populateShippingDetails,
    queryParams,
    setQueryParams,
    currentWorkflowId,
    handleFailoverSync,
    hrisIntegrationId,
    hasTriggeredFailoverSync,
    isRunningFailoverSync,
    hasShownErrorToast,
    hasHrisIntegrationSynced,
    isEmbedded,
    t,
    i18n.language,
    isAdpWfnCanada,
  ]);

  const handleUnlockEmployee = () => {
    queryParams.delete(SelectionParamKeys.EmployeeId);
    setQueryParams(queryParams, {
      replace: true,
    });
  };

  const isPolling = Boolean(currentWorkflowId);

  const isSyncButtonVisible = (slug: string | undefined) => {
    return (
      slug === HRISProviderSlugEnum.Justworksdirect ||
      slug === HRISProviderSlugEnum.TrinetPeodirect ||
      slug === HRISProviderSlugEnum.Isolved
    );
  };

  return (
    <Flex width={width} gap={spaces.space_s}>
      <Box flexGrow="1">
        {storefrontCheckoutForm && (
          <Flex width="100%" position="relative">
            <SearchAsyncSelectField
              label={t('sections.employee.searchLabel')}
              id="storefront-employee-select"
              key={`${storefrontCheckoutForm?.getValues('employee')?.value} ${employeeId} ${i18n.language}`} // also remount on language switch: react-select's cacheOptions keeps stale-language options cached per search term otherwise
              name="employee"
              control={storefrontCheckoutForm?.control}
              value={storefrontCheckoutForm?.getValues('employee')}
              defaultOptions={!employeeId}
              cacheOptions
              customComponents={{
                Option: option =>
                  option.data.value === EMPLOYEE_SEARCH_HINT_VALUE ? (
                    <SearchHintOption label={option.data.label} />
                  ) : (
                    <EmployeeSelectOption data={option.data} />
                  ),
              }}
              placeholder={t('sections.employee.placeholder')}
              noOptionsText={t('sections.employee.noResults')}
              fetchOptions={async term => {
                const employees = await searchForEmployees({
                  organizationId,
                  ...(term && { term }),
                  offset: 0,
                  limit: 20, // only the first 20 results
                  ...(!term && {
                    orderBy: 'last_name',
                    sortOrder: 'asc',
                  }),
                }).unwrap();

                const options = transformEmployeesToOptions(
                  employees,
                  i18n.language,
                  isAdpWfnCanada
                );
                return !term
                  ? appendSearchHint(options, t('sections.employee.searchHint'))
                  : options;
              }}
              isLoading={
                isFetchingEmployees ||
                isSearchingEmployees ||
                isFetchingEmployee ||
                isLoadingOrganizationPackages
              }
              isSearchable
              isClearable
              disabled={Boolean(employeeId)}
              onChange={(employee, meta) => {
                // when the new selected employee has address and it's from another country, clear all carts
                if (
                  employee?.address?.country &&
                  storefrontCheckoutForm.getValues('employee')?.address
                    ?.country !== employee.address.country
                ) {
                  clearAllCarts();
                }

                // set the new selected employee and auto-populate the shipping details
                if (employee) {
                  storefrontCheckoutForm.setValue('employee', employee, {
                    shouldValidate: true, // forcing validation, specially when an error is manually set by the otherCountryFlow
                  });
                  populateShippingDetails(employee);
                }

                // when the employee is removed
                if (meta.action === 'clear') {
                  storefrontCheckoutForm.resetField('employee');
                  resetShippingDetails();
                  clearAllCarts();
                }
              }}
            />
            {isSyncButtonVisible(provider?.slug) && (
              <SyncButton
                id="manual-sync-button"
                iconBefore="refresh"
                onClick={() => handleSync()}
                loading={isSyncing || isPolling}
                disabled={isLoadingProvider || isSyncing || isPolling}
                variant="link">
                {isPolling
                  ? t('sections.employee.syncInProgress')
                  : isEmbedded
                    ? t('sections.employee.syncButtonNoProvider')
                    : t('sections.employee.syncButton', {
                        provider: provider?.name,
                      })}
              </SyncButton>
            )}
          </Flex>
        )}
      </Box>
      {Boolean(employeeId) && (
        <Tooltip
          content={t('sections.employee.selectAnotherEmployee')}
          placement="bottom">
          <IconButton
            id="select-another-employee-button"
            icon="lock-open"
            iconSize="medium"
            flexShrink="0"
            mt="2.35rem" // need to use margin-top instead of "align-items:center" to align vertically with Search employee component (specially when the input error message appears)
            onClick={handleUnlockEmployee}
            aria-label={t('sections.employee.selectAnotherEmployee')}
            alt={t('sections.employee.selectAnotherEmployee')}
          />
        </Tooltip>
      )}
    </Flex>
  );
};
